import {
  FAMILIES,
  GEAR_SLOTS,
  GEAR_SLOT_NAME,
  INVENTORY,
  ITEMS,
  ITEM_IDS,
  RECIPES,
  RECIPE_CATEGORIES,
  RECIPE_CATEGORY_NAME,
  TILES,
  count,
  craftProblem,
  familyName,
  iconTile,
  isItemId,
  placesAs,
  slotOf,
  slotsUsed,
  usedIn,
  type Family,
  type Gear,
  type GearSlot,
  type Inventory,
  type ItemId,
  type Recipe,
  type RecipeCategory,
} from "@dyson/shared";
import { drawItemIcon } from "../render/TileAtlas";
import { slotFromDrag } from "./Hud";

/**
 * Inventory and crafting, opened with I. Shows what you wear (six slots:
 * head, body, legs, hands, feet and back; click a wearable item to put it on,
 * a worn one to take it off), lists what you carry that isn't on the
 * quick-access bar (drag between the two), and the recipes, one category to
 * a tab (the search box looks through all of them; "Can make" leaves out what
 * you can't make right now); recipes marked "workbench" only work standing by one.
 * (A chest, right clicked, opens in the pack panel: see PackPanel.) Below it all is the guide: every item and what it's for.
 */
export class CraftingPanel {
  onCraft: (recipeId: string) => void = () => {};
  /** A quick-access slot was dragged back into the inventory: take it off the bar. */
  onReturn: (slot: number) => void = () => {};
  /** An inventory item was clicked (or tapped): put it in the selected quick-access slot. */
  onPick: (item: ItemId) => void = () => {};
  /** Put on a wearable item you carry (clicked, or dropped on its slot). */
  onEquip: (item: ItemId) => void = () => {};
  /** Take off what's worn in a slot (clicked). */
  onUnequip: (slot: GearSlot) => void = () => {};

  private readonly root = document.getElementById("craft")!;
  private readonly items = this.root.querySelector<HTMLElement>(".craft-items")!;
  private readonly gearSlots = this.root.querySelector<HTMLElement>(".gear-slots")!;
  private readonly recipes = this.root.querySelector<HTMLElement>(".craft-recipes")!;
  private readonly tabs = this.root.querySelector<HTMLElement>(".craft-tabs")!;
  private readonly recipeFilter = this.root.querySelector<HTMLInputElement>(".craft-filter")!;
  private readonly canMakeBox = this.root.querySelector<HTMLInputElement>(".craft-can-make")!;
  /** The recipe tab showing, and whether only what can be made now is listed (remembered in this browser). */
  private readonly families = this.root.querySelector<HTMLElement>(".craft-families")!;
  private tab: RecipeCategory;
  private canMake: boolean;
  /** Which family's building blocks and machines are listed. */
  private family: Family;
  private readonly bench = this.root.querySelector<HTMLElement>(".craft-bench")!;
  private readonly packLabel = this.root.querySelector<HTMLElement>(".craft-pack")!;
  private readonly guide = this.root.querySelector<HTMLElement>(".guide")!;
  private readonly guideToggle = this.root.querySelector<HTMLButtonElement>(".guide-toggle")!;
  private readonly guideFilter = this.root.querySelector<HTMLInputElement>(".guide-filter")!;
  private readonly guideList = this.root.querySelector<HTMLElement>(".guide-list")!;
  /** Guide rows with the lower-case text the filter matches against; built the first time it's opened. */
  private guideRows: { row: HTMLElement; text: string }[] = [];
  /** What the panel last drew, so it only redraws when something changed. */
  private drawn = "";
  /** The item under the mouse, so pressing 1-9 can put it on the quick-access bar. */
  hovered: ItemId | null = null;

  constructor() {
    ({ tab: this.tab, canMake: this.canMake, family: this.family } = loadCraftView());
    this.root.querySelector(".craft-close")!.addEventListener("click", () => this.close());
    this.guideToggle.addEventListener("click", () => {
      const open = this.guide.classList.toggle("hidden") === false;
      this.guideToggle.textContent = open ? "Guide ▾" : "Guide ▸";
      if (open && !this.guideRows.length) this.buildGuide();
    });
    this.guideFilter.addEventListener("input", () => this.filterGuide());
    // Escape leaves a filter box (the game ignores keys while it has focus).
    for (const box of [this.guideFilter, this.recipeFilter])
      box.addEventListener("keydown", (e) => {
        if (e.key === "Escape" || e.key === "Enter") box.blur();
      });
    this.recipeFilter.addEventListener("input", () => (this.drawn = ""));
    this.canMakeBox.checked = this.canMake;
    this.canMakeBox.addEventListener("change", () => {
      this.canMake = this.canMakeBox.checked;
      saveCraftView({ tab: this.tab, canMake: this.canMake, family: this.family });
      this.drawn = "";
    });
    this.root.addEventListener("dragover", (e) => e.preventDefault());
    this.root.addEventListener("drop", (e) => {
      e.preventDefault();
      const data = e.dataTransfer?.getData("text/plain") ?? "";
      const slot = slotFromDrag(data);
      const ontoGear = !!(e.target as HTMLElement).closest(".gear-slots");
      if (slot !== null) this.onReturn(slot);
      // An item dropped on what you wear: put it on (the server checks it's wearable, and that you have it).
      else if (ontoGear && isItemId(data)) this.onEquip(data);
    });
  }

  get isOpen(): boolean {
    return !this.root.classList.contains("hidden");
  }

  /**
   * Open or close it. "all" (I) is everything: what you wear and carry, and
   * crafting; "crafting" (C) is the recipes on their own (what you
   * carry is in the pack, Tab). Asked for in the other way while it's open,
   * it switches rather than closing.
   */
  toggle(mode: "all" | "crafting" = "all"): void {
    const was = this.root.classList.contains("crafting-only") ? "crafting" : "all";
    if (this.isOpen && was === mode) this.close();
    else {
      this.root.classList.toggle("crafting-only", mode === "crafting");
      this.root.classList.remove("hidden");
    }
    this.drawn = "";
  }

  close(): void {
    this.hovered = null;
    this.root.classList.add("hidden");
  }

  /** Redraw if anything changed. `onBar` is what's on the quick-access bar: those items live there, not here. `gear`: what you wear. */
  update(inv: Inventory, atBench: boolean, onBar: Set<ItemId>, gear: Gear): void {
    if (!this.isOpen) return;
    const key = JSON.stringify(inv) + atBench + [...onBar].join() + JSON.stringify(gear) + this.tab + this.canMake + this.family + this.recipeFilter.value;
    if (key === this.drawn) return;
    this.drawn = key;

    this.gearSlots.replaceChildren(...GEAR_SLOTS.map((slot) => this.gearCell(slot, gear[slot])));

    this.bench.textContent = atBench ? "At a workbench: everything can be made here." : "Stand by a workbench for the recipes marked workbench.";
    this.bench.classList.toggle("ok", atBench);

    const used = slotsUsed(inv);
    this.packLabel.textContent = `Pack: ${used}/${INVENTORY.slots} stacks`;
    this.packLabel.classList.toggle("warn", used >= INVENTORY.slots);

    this.items.replaceChildren();
    this.hovered = null;
    const held = ITEM_IDS.filter((id) => count(inv, id) > 0 && !onBar.has(id));
    if (!held.length) this.items.textContent = onBar.size ? "Everything you carry is on the quick-access bar." : "Nothing yet. Dig rock, ore, coal and plants to collect them.";
    for (const id of held) this.items.append(this.itemCell(id, count(inv, id)));

    this.drawRecipes(inv, atBench);
  }

  /**
   * The tabs and the recipes under the open one. Building blocks and
   * machines list one family at a time (the family buttons). A search looks
   * through every tab and family, matching what a recipe makes or anything
   * it takes.
   */
  private drawRecipes(inv: Inventory, atBench: boolean): void {
    const query = this.recipeFilter.value.trim().toLowerCase();
    const makeable = (r: Recipe) => craftProblem(inv, r, atBench) === null;
    const pick = (change: () => void) => () => {
      change();
      saveCraftView({ tab: this.tab, canMake: this.canMake, family: this.family });
      this.drawn = "";
    };

    this.tabs.replaceChildren(
      ...RECIPE_CATEGORIES.map((cat) =>
        chip(
          RECIPE_CATEGORY_NAME[cat],
          cat === this.tab && !query,
          pick(() => {
            this.tab = cat;
            this.recipeFilter.value = "";
          }),
        ),
      ),
    );

    // Building blocks and machines come in every family: show one family's at a time (a search looks through them all).
    const byFamily = !query && RECIPES.some((r) => r.category === this.tab && r.family);
    this.families.classList.toggle("hidden", !byFamily);
    this.families.replaceChildren(
      ...(byFamily
        ? FAMILIES.map((f) =>
            chip(
              familyName(f),
              f === this.family,
              pick(() => (this.family = f)),
            ),
          )
        : []),
    );

    const shown = RECIPES.filter(
      (r) =>
        (query ? recipeText(r).includes(query) : r.category === this.tab && (!r.family || r.family === this.family)) &&
        (!this.canMake || makeable(r)),
    );
    this.recipes.replaceChildren();
    if (!shown.length) {
      const none = document.createElement("div");
      none.className = "small craft-none";
      none.textContent = query
        ? `Nothing${this.canMake ? " you can make now" : ""} matches "${this.recipeFilter.value.trim()}".`
        : this.canMake
          ? `Nothing here you can make right now${byFamily ? ` in ${familyName(this.family)}` : ""}.`
          : "Nothing here.";
      this.recipes.append(none);
      return;
    }
    // A search mixes tabs, so each row's tab is named on it.
    for (const r of shown) this.recipes.append(this.recipeRow(r, inv, atBench, !!query));
  }

  /** One recipe: what it makes, what it takes (and how much of that you have, where it's short), and its Craft button. */
  private recipeRow(r: Recipe, inv: Inventory, atBench: boolean, showCategory: boolean): HTMLElement {
    const problem = craftProblem(inv, r, atBench);
    const row = document.createElement("div");
    row.className = "recipe" + (problem ? " locked" : "");
    const needs = (Object.keys(r.needs) as ItemId[])
      .map((id) => `${r.needs[id]} ${ITEMS[id].name}` + (count(inv, id) < r.needs[id]! ? ` (have ${count(inv, id)})` : ""))
      .join(", ");
    const text = document.createElement("div");
    text.className = "recipe-text";
    text.innerHTML = `<b>${r.count > 1 ? `${r.count}× ` : ""}${ITEMS[r.makes].name}</b><span class="small">${needs}</span>`;
    const tags = [...(showCategory ? [RECIPE_CATEGORY_NAME[r.category].toLowerCase()] : []), ...(r.workbench ? ["workbench"] : [])];
    for (const t of tags) {
      const tag = document.createElement("span");
      tag.className = "tag";
      tag.textContent = t;
      text.querySelector("b")!.append(" ", tag);
    }
    const button = document.createElement("button");
    button.textContent = "Craft";
    button.disabled = !!problem;
    button.title = problem ?? "";
    button.addEventListener("click", () => this.onCraft(r.id));
    row.append(icon(r.makes), text, button);
    return row;
  }

  /**
   * The recipe book: every item, what it's for, what it goes into and how
   * it's dug. It never changes, so it's drawn once.
   */
  private buildGuide(): void {
    const names = (ids: ItemId[]) => [...new Set(ids)].map((id) => ITEMS[id].name).join(", ");
    for (const id of ITEM_IDS) {
      const def = ITEMS[id];
      const row = document.createElement("div");
      row.className = "guide-row";
      const text = document.createElement("div");
      text.className = "recipe-text";
      const lines: string[] = [];
      if (def.desc) lines.push(def.desc);
      if (def.heals) lines.push(`Eat it to heal ${def.heals}.`);
      const makes = RECIPES.filter((r) => r.makes === id);
      if (makes.length) lines.push(`Made from: ${makes.map((r) => names(Object.keys(r.needs) as ItemId[])).join("; or ")}${makes.some((r) => r.workbench) ? " (workbench)" : ""}`);
      const uses = usedIn(id);
      if (uses.length) lines.push(`Used in: ${names(uses.map((r) => r.makes))}`);
      const dig = digText(id);
      if (dig) lines.push(dig);
      const name = document.createElement("b");
      name.textContent = def.name;
      text.append(name);
      for (const line of lines) {
        const span = document.createElement("span");
        span.className = "small";
        span.textContent = line;
        text.append(span);
      }
      row.append(icon(id), text);
      this.guideList.append(row);
      this.guideRows.push({ row, text: `${def.name} ${lines.join(" ")}`.toLowerCase() });
    }
    this.filterGuide();
  }

  private filterGuide(): void {
    const q = this.guideFilter.value.trim().toLowerCase();
    for (const { row, text } of this.guideRows) row.classList.toggle("hidden", !!q && !text.includes(q));
  }

  /**
   * An inventory item: drag it onto the quick-access bar, or hover it and
   * press 1-9. Something wearable is put on with a click.
   */
  private itemCell(id: ItemId, n: number): HTMLElement {
    const cell = document.createElement("div");
    cell.className = "item";
    const wearable = !!slotOf(id);
    cell.title = wearable
      ? `${ITEMS[id].name}: click to put it on (${GEAR_SLOT_NAME[slotOf(id)!].toLowerCase()}), or drag it onto the bar`
      : `${ITEMS[id].name}: click to put it in the selected slot, drag it onto the bar, or hover and press 1-9`;
    cell.draggable = true;
    cell.addEventListener("dragstart", (e) => e.dataTransfer?.setData("text/plain", id));
    cell.addEventListener("mouseenter", () => (this.hovered = id));
    cell.addEventListener("mouseleave", () => (this.hovered = this.hovered === id ? null : this.hovered));
    if (wearable) cell.addEventListener("click", () => this.onEquip(id));
    // A click (or a tap: there's no dragging on a touch screen) puts it in the selected slot.
    else cell.addEventListener("click", () => this.onPick(id));
    cell.append(icon(id), countLabel(n));
    return cell;
  }

  /** One of the slots you wear things in: what's there (click to take it off), or its name when it's empty. */
  private gearCell(slot: GearSlot, worn: ItemId | undefined): HTMLElement {
    const cell = document.createElement("div");
    cell.className = "item gear-slot" + (worn ? " worn" : "");
    cell.dataset.slot = slot;
    cell.title = worn ? `${ITEMS[worn].name} (${GEAR_SLOT_NAME[slot].toLowerCase()}): click to take it off` : `${GEAR_SLOT_NAME[slot]}: nothing worn. Drop something wearable here, or click it below.`;
    if (worn) {
      cell.append(icon(worn));
      cell.addEventListener("click", () => this.onUnequip(slot));
    } else {
      const empty = document.createElement("span");
      empty.className = "empty";
      cell.append(empty);
    }
    const label = document.createElement("span");
    label.className = "label";
    label.textContent = GEAR_SLOT_NAME[slot];
    cell.append(label);
    return cell;
  }
}

/** A tab or family button. */
function chip(label: string, active: boolean, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "craft-tab" + (active ? " active" : "");
  b.setAttribute("aria-pressed", String(active));
  b.textContent = label;
  b.addEventListener("click", onClick);
  return b;
}

/** What a recipe search matches against: what it makes, the family it's in, and everything it takes. */
function recipeText(r: Recipe): string {
  const takes = (Object.keys(r.needs) as ItemId[]).map((id) => ITEMS[id].name);
  return [ITEMS[r.makes].name, ...(r.family ? [familyName(r.family)] : []), ...takes].join(" ").toLowerCase();
}

const CRAFT_VIEW_KEY = "dyson.craftView";

/** How the recipes are being looked at: the tab, "Can make", and the family picked for blocks and machines. */
interface CraftView {
  tab: RecipeCategory;
  canMake: boolean;
  family: Family;
}

/** The recipe view as this browser last left it. */
function loadCraftView(): CraftView {
  const fresh: CraftView = { tab: RECIPE_CATEGORIES[0]!, canMake: false, family: FAMILIES[0]! };
  try {
    const saved = JSON.parse(localStorage.getItem(CRAFT_VIEW_KEY) ?? "null") as Record<string, unknown> | null;
    return {
      tab: RECIPE_CATEGORIES.find((c) => c === saved?.tab) ?? fresh.tab,
      canMake: saved?.canMake === true,
      family: FAMILIES.find((f) => f === saved?.family) ?? fresh.family,
    };
  } catch {
    return fresh;
  }
}

function saveCraftView(view: CraftView): void {
  try {
    localStorage.setItem(CRAFT_VIEW_KEY, JSON.stringify(view));
  } catch {
    /* storage blocked: the view just won't be remembered */
  }
}

/**
 * How an item is dug, for the guide: from the block it places as, or else the
 * natural block it's dug out of ("Found as Ore: dig with a stone pickaxe").
 */
function digText(id: ItemId): string | null {
  const own = placesAs(id);
  const tile = iconTile(id);
  const def = tile === undefined ? undefined : TILES[tile];
  if (!def) return null;
  const tier = def.minTier && def.minTier !== "wood" ? `${def.minTier} ` : "";
  const how = !def.tool ? "dig with anything, even bare hands" : def.needsTool ? `dig with a ${tier}${def.tool}` : `dig fastest with a ${tier}${def.tool} (bare hands work, slowly)`;
  return own === undefined ? `Found as ${def.name}: ${how}.` : `Placed as a block: ${how}.`;
}

function countLabel(n: number): HTMLElement {
  const label = document.createElement("span");
  label.className = "count";
  label.textContent = String(n);
  return label;
}

function icon(id: ItemId): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = c.height = 32;
  drawItemIcon(id, c);
  return c;
}
